import { Fragment, useEffect, useRef } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CalendarDays, Clock } from "lucide-react";
import { useI18n } from "@/i18n";
import { useMeta } from "@/lib/useMeta";
import { PublicPage } from "@/components/site";
import { LANGS } from "@/i18n";
import { postsFor, findPost, slugFor } from "@/content/blogPosts";

const LINK = /\[([^\]]+)\]\(([^)]+)\)/g;

function Rich({ text }) {
  const out = [];
  let last = 0;
  for (const m of text.matchAll(LINK)) {
    out.push(text.slice(last, m.index));
    out.push(<Link key={m.index} to={m[2]} className="text-emerald-400 underline underline-offset-4 hover:text-emerald-300">{m[1]}</Link>);
    last = m.index + m[0].length;
  }
  out.push(text.slice(last));
  return out.map((x, i) => <Fragment key={i}>{x}</Fragment>);
}

function Block({ b }) {
  if (typeof b === "string") return <p><Rich text={b} /></p>;
  const List = b.ol ? "ol" : "ul";
  return (
    <List className={`${b.ol ? "list-decimal" : "list-disc"} pl-6 space-y-2 marker:text-emerald-500`}>
      {(b.ol || b.ul).map((x) => <li key={x}><Rich text={x} /></li>)}
    </List>
  );
}

function PostMeta({ p }) {
  const { t, fmtDate } = useI18n();
  return (
    <p className="flex flex-wrap items-center gap-4 text-xs text-neutral-400">
      <span className="flex items-center gap-1.5"><CalendarDays size={14} /><time dateTime={p.date}>{fmtDate(p.date)}</time></span>
      <span className="flex items-center gap-1.5"><Clock size={14} />{t("blog.minRead", { n: p.readMin })}</span>
    </p>
  );
}

function PostCard({ p, i }) {
  const { t } = useI18n();
  return (
    <article className="panel overflow-hidden flex flex-col fade-up hover:border-emerald-700/60 transition-colors duration-300" style={{ animationDelay: `${i * 80}ms` }} data-testid={`blog-card-${p.id}`}>
      <Link to={`/blog/${p.slug}`} className="block aspect-[16/9] overflow-hidden bg-[var(--bg-2)]">
        <img src={p.cover.replace("w=1400", "w=800")} alt={p.coverAlt} loading="lazy" className="w-full h-full object-cover hover:scale-[1.03] transition-transform duration-500" />
      </Link>
      <div className="p-6 flex flex-col flex-1">
        <PostMeta p={p} />
        <h2 className="mt-3 text-lg font-semibold leading-snug"><Link to={`/blog/${p.slug}`} className="hover:text-emerald-400 transition-colors">{p.title}</Link></h2>
        <p className="mt-3 text-sm text-neutral-400 flex-1">{p.excerpt}</p>
        <Link to={`/blog/${p.slug}`} className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-400 hover:text-emerald-300" data-testid={`blog-read-${p.id}`}>
          {t("blog.readMore")} <ArrowRight size={16} />
        </Link>
      </div>
    </article>
  );
}

export function BlogList() {
  const { t, lang } = useI18n();
  const posts = postsFor(lang);
  useMeta(t("meta.blog.title"), t("meta.blog.desc"), { page: "blog" });
  return (
    <PublicPage testid="blog-page">
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <p className="eyebrow mb-4">{t("nav.blog")}</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold" data-testid="blog-title">{t("blog.title")}</h1>
        <p className="mt-4 text-neutral-400 text-base md:text-lg max-w-2xl">{t("blog.sub")}</p>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((p, i) => <PostCard key={p.slug} p={p} i={i} />)}
        </div>
      </section>
    </PublicPage>
  );
}

function PostCta() {
  const { t } = useI18n();
  return (
    <aside className="light-panel p-8 mt-14" data-testid="blog-cta">
      <h2 className="text-xl font-semibold">{t("blog.ctaTitle")}</h2>
      <p className="mt-2 text-sm text-neutral-600">{t("blog.ctaText")}</p>
      <Link to="/register" className="btn-brand mt-5 rounded-full px-6 py-3 font-semibold inline-flex items-center gap-2" data-testid="blog-cta-btn">{t("blog.ctaBtn")} <ArrowRight size={16} /></Link>
    </aside>
  );
}

// URL decides the language of a post: opening a German slug switches the UI to German.
// Only an explicit language pick by the visitor navigates to the same post in the new language.
function useLangSync(p, slug) {
  const { lang, setLang, userSwitch } = useI18n();
  const nav = useNavigate();
  const seenSwitch = useRef(userSwitch);
  const pending = useRef(null);
  useEffect(() => {
    if (!p) return;
    if (seenSwitch.current !== userSwitch) {
      seenSwitch.current = userSwitch;
      const s = slugFor(p.id, lang);
      if (s && s !== slug) {
        pending.current = s;
        nav(`/blog/${s}`, { replace: true });
      }
      return;
    }
    if (pending.current) {
      if (pending.current !== slug) return;
      pending.current = null;
    }
    if (p.lang !== lang) setLang(p.lang);
  }, [p, lang, slug, userSwitch, nav, setLang]);
  useEffect(() => {
    if (!p) return undefined;
    const links = [...LANGS, "x-default"].map((l) => {
      const el = document.createElement("link");
      el.rel = "alternate";
      el.hreflang = l;
      el.href = `${window.location.origin}/blog/${slugFor(p.id, l === "x-default" ? "tr" : l)}`;
      document.head.appendChild(el);
      return el;
    });
    return () => links.forEach((el) => el.remove());
  }, [p]);
}

export function BlogPost() {
  const { slug } = useParams();
  const { t } = useI18n();
  const p = findPost(slug);
  useLangSync(p, slug);
  useMeta(p ? p.metaTitle : t("meta.blog.title"), p ? p.metaDescription : t("meta.blog.desc"), { image: p?.cover, type: "article" });
  if (!p) return <Navigate to="/blog" replace />;
  const ld = { "@context": "https://schema.org", "@type": "BlogPosting", headline: p.title, description: p.metaDescription, image: p.cover,
    datePublished: p.date, inLanguage: { tr: "tr-TR", en: "en", de: "de-DE" }[p.lang], author: { "@type": "Organization", name: "woodesing" }, publisher: { "@type": "Organization", name: "woodesing" } };
  return (
    <PublicPage testid="blog-post-page">
      <article className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <script type="application/ld+json">{JSON.stringify(ld)}</script>
        <nav aria-label="breadcrumb" className="text-xs text-neutral-500 flex flex-wrap gap-1.5">
          <Link to="/" className="hover:text-white">{t("nav.home")}</Link><span>/</span>
          <Link to="/blog" className="hover:text-white" data-testid="breadcrumb-blog">{t("nav.blog")}</Link><span>/</span>
          <span className="text-neutral-400 truncate max-w-[60vw]">{p.title}</span>
        </nav>
        <h1 className="mt-6 text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight" data-testid="blog-post-title">{p.title}</h1>
        <div className="mt-5"><PostMeta p={p} /></div>
        <img src={p.cover} alt={p.coverAlt} className="mt-8 rounded-xl w-full aspect-[16/9] object-cover" data-testid="blog-post-cover" />
        <div className="mt-10 space-y-10 text-neutral-300 leading-relaxed text-[15px] sm:text-base" lang={p.lang}>
          {p.sections.map((s) => (
            <section key={s.h2} className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-semibold text-white">{s.h2}</h2>
              {s.body.map((b, i) => <Block key={i} b={b} />)}
            </section>
          ))}
        </div>
        <PostCta />
        <section className="mt-14">
          <h2 className="text-lg font-semibold">{t("blog.related")}</h2>
          <ul className="mt-4 space-y-3">
            {postsFor(p.lang).filter((x) => x.id !== p.id).map((x) => (
              <li key={x.slug}><Link to={`/blog/${x.slug}`} className="text-emerald-400 hover:underline" data-testid={`related-${x.id}`}>{x.title}</Link></li>
            ))}
          </ul>
          <Link to="/blog" className="mt-8 inline-flex items-center gap-1.5 text-sm text-neutral-400 hover:text-white" data-testid="back-to-blog"><ArrowLeft size={16} />{t("blog.back")}</Link>
        </section>
      </article>
    </PublicPage>
  );
}
